import { useState } from "react";

const BASE_RATES = {
  dry_van: 838, liftgate_van: 1020, curtain_side: 868, intermodal: 679,
  reefer: 1264, reefer_liftgate: 1450,
  flatbed: 771, step_deck: 923, conestoga: 1050, double_drop: 1100,
  rgn: 1800, lowboy: 1633,
  pup_28: 620, pup_doubles: 1180,
  tanker: 1679, pneumatic: 1450
};

const TYPE_INFO = {
  liftgate_van: "28ft van with hydraulic liftgate. Premium over standard dry van due to liftgate equipment.",
  reefer_liftgate: "Temperature-controlled trailer with hydraulic liftgate. Highest rate in the reefer category.",
  conestoga: "Flatbed with rolling tarp system. Protects cargo while maintaining flatbed loading flexibility.",
  double_drop: "Lower deck height than step deck. Used for oversized height-sensitive loads.",
  rgn: "Removable Gooseneck — detachable front allows front-loading of heavy equipment.",
  pup_28: "28ft pup trailer used in doubles/triples configurations or urban deliveries.",
  pup_doubles: "Two 28ft pup trailers priced as a set. Discount vs two individual pups.",
  pneumatic: "Pressurized tank for dry bulk materials like grain, cement, flour, or plastic pellets."
};

const LENGTH_OPTIONS = {
  dry_van: [48, 53], liftgate_van: [28], curtain_side: [48, 53],
  intermodal: [20, 40, 53], reefer: [48, 53], reefer_liftgate: [48, 53],
  flatbed: [48, 53], step_deck: [48, 53], conestoga: [48, 53],
  double_drop: [48], rgn: [40, 48, 53], lowboy: [25, 29, 35, 48],
  pup_28: [28], pup_doubles: [28], tanker: [40, 43, 45], pneumatic: [40, 43]
};

const REGION_FACTORS = {
  northeast: 1.12, west_coast: 1.15, mountain: 1.05,
  southwest: 1.02, midwest: 0.97, southeast: 0.95
};

const TERM_DISCOUNTS = { 12: 1.0, 24: 0.97, 36: 0.94, 48: 0.91, 60: 0.88 };
const CONDITION_FACTORS = { new: 1.15, like_new: 1.05, good: 1.0, fair: 0.88 };

const REGION_NAMES = {
  northeast: "Northeast", west_coast: "West Coast", mountain: "Mountain",
  southwest: "Southwest", midwest: "Midwest", southeast: "Southeast"
};

const TYPE_LABELS = {
  dry_van: "Dry Van", liftgate_van: "Liftgate Van", curtain_side: "Curtain Side",
  intermodal: "Intermodal", reefer: "Reefer", reefer_liftgate: "Reefer + Liftgate",
  flatbed: "Flatbed", step_deck: "Step Deck", conestoga: "Conestoga",
  double_drop: "Double Drop", rgn: "RGN", lowboy: "Lowboy",
  pup_28: "Pup (28ft)", pup_doubles: "Doubles Set",
  tanker: "Tanker", pneumatic: "Pneumatic Bulk"
};

const CAT_LABELS = {
  dry_van: "Dry Van", liftgate_van: "Dry Van", curtain_side: "Dry Van", intermodal: "Dry Van",
  reefer: "Reefer", reefer_liftgate: "Reefer",
  flatbed: "Flatbed", step_deck: "Flatbed", conestoga: "Flatbed",
  double_drop: "Flatbed", rgn: "Flatbed", lowboy: "Flatbed",
  pup_28: "Pup", pup_doubles: "Pup",
  tanker: "Tanker", pneumatic: "Tanker"
};

function calculate(state) {
  const { trailerType, length, year, condition, leaseTerm, region, airRide, maintenance, gps } = state;
  const age = 2025 - parseInt(year);
  const base = BASE_RATES[trailerType];
  const ageFactor = Math.max(0.7, 1.0 - age * 0.03);
  const afterAge = Math.round(base * ageFactor);
  const termFactor = TERM_DISCOUNTS[parseInt(leaseTerm)];
  const afterTerm = Math.round(afterAge * termFactor);
  const regionFactor = REGION_FACTORS[region];
  const afterRegion = Math.round(afterTerm * regionFactor);
  const condFactor = CONDITION_FACTORS[condition];
  const afterCond = Math.round(afterRegion * condFactor);
  const airAdj = airRide ? Math.round(base * 0.05) : 0;
  const maintAdj = maintenance ? Math.round(base * 0.08) : 0;
  const gpsAdj = gps ? 35 : 0;
  const lengthAdj = parseInt(length) >= 53 ? Math.round(base * 0.03) : 0;
  const price = afterCond + airAdj + maintAdj + gpsAdj + lengthAdj;
  return {
    price, base,
    low: Math.round(price * 0.9),
    high: Math.round(price * 1.1),
    daily: (price / 30).toFixed(2),
    annual: price * 12,
    total: price * parseInt(leaseTerm),
    ageAdj: afterAge - base,
    termAdj: afterTerm - afterAge,
    regionAdj: afterRegion - afterTerm,
    condAdj: afterCond - afterRegion,
    airAdj, maintAdj, gpsAdj, lengthAdj,
    age
  };
}

export default function App() {
  const [tab, setTab] = useState("calculator");
  const [modelInfo, setModelInfo] = useState({ name: "Default (market data Jun 2025)", r2: "0.88", rmse: "$126", rows: "500", variance: "88%" });
  const [baseRates, setBaseRates] = useState({ ...BASE_RATES });
  const [form, setForm] = useState({
    trailerType: "dry_van", length: "53", year: "2016",
    condition: "good", leaseTerm: "36", region: "midwest",
    airRide: false, maintenance: false, gps: false
  });

  const update = (key, val) => {
    const newForm = { ...form, [key]: val };
    if (key === "trailerType") {
      const lengths = LENGTH_OPTIONS[val];
      newForm.length = String(lengths[lengths.length - 1]);
    }
    setForm(newForm);
  };

  const result = calculate(form);
  const maxRate = Math.max(...Object.values(baseRates));

  const loadModel = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        if (data.base_rates) setBaseRates({ ...baseRates, ...data.base_rates });
        setModelInfo({
          name: file.name,
          r2: data.model_performance?.r2 || "—",
          rmse: data.model_performance?.rmse ? "$" + data.model_performance.rmse : "—",
          rows: data.model_performance?.training_rows || "—",
          variance: data.model_performance?.variance_explained ? data.model_performance.variance_explained + "%" : "—"
        });
        alert("Pricing model updated! All rates have been refreshed.");
      } catch { alert("Invalid JSON. Please export from the AI Agent tool."); }
    };
    reader.readAsText(file);
  };

  return (
    <div style={s.page}>
      <div style={s.container}>
        <div style={s.header}>
          <h1 style={s.title}>🚛 Trailer Lease Pricing Tool</h1>
          <p style={s.subtitle}>Market-calibrated pricing · Updated from AI Agent analysis</p>
        </div>

        <div style={s.tabs}>
          {["calculator", "market", "model"].map(t => (
            <button key={t} onClick={() => setTab(t)}
              style={{ ...s.tab, ...(tab === t ? s.tabActive : {}) }}>
              {t === "calculator" ? "Calculator" : t === "market" ? "Market Rates" : "Model Settings"}
            </button>
          ))}
        </div>

        {tab === "calculator" && (
          <div>
            <div style={s.card}>
              <div style={s.cardTitle}>⚙️ Trailer Specifications</div>
              <div style={s.field}>
                <label style={s.label}>Trailer Type</label>
                <select style={s.select} value={form.trailerType} onChange={e => update("trailerType", e.target.value)}>
                  <optgroup label="Dry Van">
                    <option value="dry_van">Dry Van (48/53ft)</option>
                    <option value="liftgate_van">Liftgate Van (28ft)</option>
                    <option value="curtain_side">Curtain Side (48/53ft)</option>
                    <option value="intermodal">Intermodal (20/40/53ft)</option>
                  </optgroup>
                  <optgroup label="Reefer">
                    <option value="reefer">Reefer (48/53ft)</option>
                    <option value="reefer_liftgate">Reefer with Liftgate (48/53ft)</option>
                  </optgroup>
                  <optgroup label="Flatbed">
                    <option value="flatbed">Flatbed (48/53ft)</option>
                    <option value="step_deck">Step Deck (48/53ft)</option>
                    <option value="conestoga">Conestoga (48/53ft)</option>
                    <option value="double_drop">Double Drop (48ft)</option>
                    <option value="rgn">RGN — Removable Gooseneck</option>
                    <option value="lowboy">Lowboy (25-55ft)</option>
                  </optgroup>
                  <optgroup label="Pup / Doubles">
                    <option value="pup_28">Pup Trailer (28ft)</option>
                    <option value="pup_doubles">Doubles Set (2x 28ft)</option>
                  </optgroup>
                  <optgroup label="Tanker">
                    <option value="tanker">Tanker</option>
                    <option value="pneumatic">Pneumatic Bulk</option>
                  </optgroup>
                </select>
              </div>

              {TYPE_INFO[form.trailerType] && (
                <div style={s.infoBox}>{TYPE_INFO[form.trailerType]}</div>
              )}

              <div style={s.grid2}>
                <div style={s.field}>
                  <label style={s.label}>Length</label>
                  <select style={s.select} value={form.length} onChange={e => update("length", e.target.value)}>
                    {(LENGTH_OPTIONS[form.trailerType] || [48, 53]).map(l => (
                      <option key={l} value={l}>{l} ft</option>
                    ))}
                  </select>
                </div>
                <div style={s.field}>
                  <label style={s.label}>Trailer Year</label>
                  <select style={s.select} value={form.year} onChange={e => update("year", e.target.value)}>
                    {[2025,2024,2023,2022,2021,2020,2019,2018,2017,2016,2015,2014,2013].map(y => (
                      <option key={y} value={y}>{y}{y === 2025 ? " (New)" : ""}</option>
                    ))}
                  </select>
                </div>
                <div style={s.field}>
                  <label style={s.label}>Condition</label>
                  <select style={s.select} value={form.condition} onChange={e => update("condition", e.target.value)}>
                    <option value="new">New</option>
                    <option value="like_new">Like New</option>
                    <option value="good">Good</option>
                    <option value="fair">Fair</option>
                  </select>
                </div>
                <div style={s.field}>
                  <label style={s.label}>Lease Term</label>
                  <select style={s.select} value={form.leaseTerm} onChange={e => update("leaseTerm", e.target.value)}>
                    {[12,24,36,48,60].map(t => (
                      <option key={t} value={t}>{t} months</option>
                    ))}
                  </select>
                </div>
                <div style={s.field}>
                  <label style={s.label}>Region</label>
                  <select style={s.select} value={form.region} onChange={e => update("region", e.target.value)}>
                    <option value="northeast">Northeast (+12%)</option>
                    <option value="west_coast">West Coast (+15%)</option>
                    <option value="mountain">Mountain (+5%)</option>
                    <option value="southwest">Southwest (+2%)</option>
                    <option value="midwest">Midwest (-3%)</option>
                    <option value="southeast">Southeast (-5%)</option>
                  </select>
                </div>
                <div style={s.field}>
                  <label style={s.label}>Air Ride Suspension</label>
                  <select style={s.select} value={form.airRide ? "1" : "0"} onChange={e => update("airRide", e.target.value === "1")}>
                    <option value="1">Yes</option>
                    <option value="0">No</option>
                  </select>
                </div>
                <div style={s.field}>
                  <label style={s.label}>Maintenance Included</label>
                  <select style={s.select} value={form.maintenance ? "1" : "0"} onChange={e => update("maintenance", e.target.value === "1")}>
                    <option value="1">Yes</option>
                    <option value="0">No</option>
                  </select>
                </div>
                <div style={s.field}>
                  <label style={s.label}>GPS Included</label>
                  <select style={s.select} value={form.gps ? "1" : "0"} onChange={e => update("gps", e.target.value === "1")}>
                    <option value="1">Yes</option>
                    <option value="0">No</option>
                  </select>
                </div>
              </div>
            </div>

            <div style={s.priceResult}>
              <div style={s.priceLabel}>Estimated Monthly Lease Rate</div>
              <div style={s.mainPrice}>${result.price.toLocaleString()}</div>
              <div style={s.priceRange}>Range: ${result.low.toLocaleString()} — ${result.high.toLocaleString()}</div>
              <div style={{ marginTop: "10px" }}>
                <span style={{
                  ...s.badge,
                  ...(Math.abs((result.price - result.base) / result.base * 100) < 5
                    ? s.badgeMarket
                    : result.price > result.base ? s.badgeAbove : s.badgeBelow)
                }}>
                  {Math.abs((result.price - result.base) / result.base * 100) < 5
                    ? "At market rate"
                    : result.price > result.base
                      ? `${Math.round((result.price - result.base) / result.base * 100)}% above base`
                      : `${Math.round((result.base - result.price) / result.base * 100)}% below base`}
                </span>
              </div>
              <div style={s.divider} />
              <div style={s.grid3}>
                <div>
                  <div style={s.metricLabel}>Daily Equivalent</div>
                  <div style={s.metricVal}>${result.daily}</div>
                </div>
                <div>
                  <div style={s.metricLabel}>Annual Cost</div>
                  <div style={s.metricVal}>${result.annual.toLocaleString()}</div>
                </div>
                <div>
                  <div style={s.metricLabel}>Total Lease Cost</div>
                  <div style={s.metricVal}>${result.total.toLocaleString()}</div>
                </div>
              </div>
            </div>

            <div style={s.card}>
              <div style={s.cardTitle}>📊 Pricing Factors</div>
              <div style={s.factorRow}>
                <span style={s.factorLabel}>Base rate ({TYPE_LABELS[form.trailerType]})</span>
                <span style={s.factorNeu}>${result.base.toLocaleString()}/mo</span>
              </div>
              {result.age > 0 && (
                <div style={s.factorRow}>
                  <span style={s.factorLabel}>Age adjustment ({result.age} yr{result.age > 1 ? "s" : ""})</span>
                  <span style={s.factorNeg}>-${Math.abs(result.ageAdj).toLocaleString()}</span>
                </div>
              )}
              {parseInt(form.leaseTerm) > 12 && (
                <div style={s.factorRow}>
                  <span style={s.factorLabel}>Lease term discount ({form.leaseTerm}mo)</span>
                  <span style={s.factorNeg}>-${Math.abs(result.termAdj).toLocaleString()}</span>
                </div>
              )}
              <div style={s.factorRow}>
                <span style={s.factorLabel}>Regional factor ({REGION_NAMES[form.region]})</span>
                <span style={result.regionAdj >= 0 ? s.factorPos : s.factorNeg}>
                  {result.regionAdj >= 0 ? "+" : "-"}${Math.abs(result.regionAdj).toLocaleString()}
                </span>
              </div>
              {form.condition !== "good" && (
                <div style={s.factorRow}>
                  <span style={s.factorLabel}>Condition ({form.condition.replace("_", " ")})</span>
                  <span style={result.condAdj >= 0 ? s.factorPos : s.factorNeg}>
                    {result.condAdj >= 0 ? "+" : "-"}${Math.abs(result.condAdj).toLocaleString()}
                  </span>
                </div>
              )}
              {form.airRide && (
                <div style={s.factorRow}>
                  <span style={s.factorLabel}>Air ride premium</span>
                  <span style={s.factorPos}>+${result.airAdj}</span>
                </div>
              )}
              {form.maintenance && (
                <div style={s.factorRow}>
                  <span style={s.factorLabel}>Maintenance included</span>
                  <span style={s.factorPos}>+${result.maintAdj}</span>
                </div>
              )}
              {form.gps && (
                <div style={s.factorRow}>
                  <span style={s.factorLabel}>GPS included</span>
                  <span style={s.factorPos}>+$35</span>
                </div>
              )}
              {parseInt(form.length) >= 53 && (
                <div style={s.factorRow}>
                  <span style={s.factorLabel}>{form.length}ft length premium</span>
                  <span style={s.factorPos}>+${result.lengthAdj}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "market" && (
          <div>
            <div style={s.card}>
              <div style={s.cardTitle}>📈 Average Monthly Rates by Type</div>
              <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "12px" }}>
                36mo lease · good condition · Midwest baseline
              </div>
              {Object.entries(baseRates)
                .sort((a, b) => b[1] - a[1])
                .map(([key, rate]) => (
                  <div key={key} style={s.marketRow}>
                    <span style={s.marketLabel}>
                      {TYPE_LABELS[key]}
                      <span style={s.catTag}>{CAT_LABELS[key]}</span>
                    </span>
                    <div style={s.barWrap}>
                      <div style={{ ...s.bar, width: `${Math.round(rate / maxRate * 100)}%` }} />
                    </div>
                    <span style={s.marketRate}>${rate.toLocaleString()}</span>
                  </div>
                ))}
            </div>

            <div style={s.card}>
              <div style={s.cardTitle}>🗺️ Regional Multipliers</div>
              {[["West Coast", "+15%", true], ["Northeast", "+12%", true], ["Mountain", "+5%", true],
                ["Southwest", "+2%", true], ["Midwest", "-3%", false], ["Southeast", "-5%", false]].map(([name, pct, above]) => (
                <div key={name} style={s.marketRow}>
                  <span style={{ color: "#cbd5e1", fontSize: "13px" }}>{name}</span>
                  <span style={{ ...s.badge, ...(above ? s.badgeAbove : s.badgeBelow) }}>{pct}</span>
                </div>
              ))}
            </div>

            <div style={s.card}>
              <div style={s.cardTitle}>🕐 Lease Term Discounts</div>
              {[[12, "No discount", false], [24, "-3%", true], [36, "-6%", true],
                [48, "-9%", true], [60, "-12%", true]].map(([term, pct, show]) => (
                <div key={term} style={s.marketRow}>
                  <span style={{ color: "#cbd5e1", fontSize: "13px" }}>{term} months</span>
                  {show
                    ? <span style={{ ...s.badge, ...s.badgeBelow }}>{pct}</span>
                    : <span style={{ color: "#64748b", fontSize: "13px" }}>{pct}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "model" && (
          <div>
            <div style={s.card}>
              <div style={s.cardTitle}>🗄️ Pricing Model</div>
              <p style={{ fontSize: "13px", color: "#94a3b8", marginBottom: "1rem" }}>
                Import a JSON pricing model exported from the AI Data Analyst Agent to update all rates automatically.
              </p>
              <label style={s.uploadArea}>
                <div style={{ fontSize: "24px", marginBottom: "8px" }}>⬆️</div>
                <p style={{ fontSize: "13px", color: "#94a3b8" }}>Click to upload pricing model JSON</p>
                <p style={{ fontSize: "11px", color: "#64748b", marginTop: "4px" }}>
                  Export from AI Agent → Run trailer analysis → Click Export Pricing Model
                </p>
                <input type="file" accept=".json" style={{ display: "none" }} onChange={loadModel} />
              </label>
              <div style={s.modelInfo}>
                <span>Model: {modelInfo.name}</span>
                <span>R² = {modelInfo.r2}</span>
              </div>
            </div>

            <div style={s.card}>
              <div style={s.cardTitle}>📉 Model Performance</div>
              <div style={s.grid4}>
                {[
                  { val: modelInfo.r2, lbl: "R² Score" },
                  { val: modelInfo.rmse, lbl: "Avg Error" },
                  { val: modelInfo.rows, lbl: "Training Rows" },
                  { val: modelInfo.variance, lbl: "Variance Explained" }
                ].map(({ val, lbl }) => (
                  <div key={lbl} style={s.metric}>
                    <div style={s.metricBigVal}>{val}</div>
                    <div style={s.metricBigLbl}>{lbl}</div>
                  </div>
                ))}
              </div>
            </div>

            <div style={s.card}>
              <div style={s.cardTitle}>📋 Top Pricing Factors (SHAP)</div>
              {[
                ["1. Length × lease term interaction", "Most impactful", "#4ade80"],
                ["2. Trailer age (years)", "Strong negative", "#f87171"],
                ["3. Length (ft)", "Positive", "#4ade80"],
                ["4. Lease term (months)", "Inverse effect", "#f87171"],
                ["5. Specialty category premium", "+$400-800/mo", "#4ade80"]
              ].map(([label, impact, color]) => (
                <div key={label} style={s.factorRow}>
                  <span style={s.factorLabel}>{label}</span>
                  <span style={{ fontSize: "13px", fontWeight: "500", color }}>{impact}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: "100vh", backgroundColor: "#0f172a", display: "flex", justifyContent: "center", padding: "40px 20px", fontFamily: "'Segoe UI', sans-serif" },
  container: { width: "100%", maxWidth: "720px" },
  header: { marginBottom: "1.5rem" },
  title: { color: "#f8fafc", fontSize: "24px", fontWeight: "600", margin: "0 0 4px" },
  subtitle: { color: "#94a3b8", fontSize: "13px", margin: 0 },
  tabs: { display: "flex", gap: "4px", marginBottom: "1.5rem", borderBottom: "1px solid #334155" },
  tab: { padding: "8px 16px", fontSize: "13px", cursor: "pointer", border: "none", background: "none", color: "#64748b", borderBottom: "2px solid transparent", marginBottom: "-1px" },
  tabActive: { color: "#f8fafc", borderBottomColor: "#f8fafc", fontWeight: "500" },
  card: { backgroundColor: "#1e293b", border: "1px solid #334155", borderRadius: "12px", padding: "1.25rem", marginBottom: "1rem" },
  cardTitle: { fontSize: "14px", fontWeight: "600", color: "#cbd5e1", marginBottom: "1rem" },
  field: { marginBottom: "12px" },
  label: { display: "block", fontSize: "12px", color: "#94a3b8", marginBottom: "6px" },
  select: { width: "100%", padding: "8px 10px", fontSize: "13px", border: "1px solid #334155", borderRadius: "8px", backgroundColor: "#0f172a", color: "#f8fafc" },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" },
  grid3: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", textAlign: "left" },
  grid4: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "12px" },
  infoBox: { backgroundColor: "#0f172a", border: "1px solid #3b82f6", borderRadius: "8px", padding: "10px 12px", fontSize: "12px", color: "#93c5fd", marginBottom: "12px" },
  priceResult: { backgroundColor: "#1e293b", border: "1px solid #334155", borderRadius: "12px", padding: "1.5rem", textAlign: "center", marginBottom: "1rem" },
  priceLabel: { fontSize: "12px", color: "#64748b", marginBottom: "8px" },
  mainPrice: { fontSize: "40px", fontWeight: "600", color: "#f8fafc" },
  priceRange: { fontSize: "13px", color: "#64748b", marginTop: "6px" },
  badge: { display: "inline-block", fontSize: "11px", padding: "3px 10px", borderRadius: "20px" },
  badgeMarket: { backgroundColor: "#1e3a5f", color: "#93c5fd" },
  badgeAbove: { backgroundColor: "#3f1515", color: "#f87171" },
  badgeBelow: { backgroundColor: "#14302a", color: "#4ade80" },
  divider: { borderTop: "1px solid #334155", margin: "1rem 0" },
  metricLabel: { fontSize: "11px", color: "#64748b" },
  metricVal: { fontSize: "16px", fontWeight: "500", color: "#f8fafc", marginTop: "2px" },
  factorRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #1e293b" },
  factorLabel: { color: "#64748b", fontSize: "13px" },
  factorPos: { color: "#4ade80", fontWeight: "500", fontSize: "13px" },
  factorNeg: { color: "#f87171", fontWeight: "500", fontSize: "13px" },
  factorNeu: { color: "#f8fafc", fontWeight: "500", fontSize: "13px" },
  marketRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #1e293b" },
  marketLabel: { color: "#cbd5e1", fontSize: "13px", minWidth: "140px" },
  marketRate: { color: "#f8fafc", fontSize: "13px", fontWeight: "500", minWidth: "55px", textAlign: "right" },
  barWrap: { flex: 1, margin: "0 12px", height: "6px", backgroundColor: "#0f172a", borderRadius: "3px", overflow: "hidden" },
  bar: { height: "100%", borderRadius: "3px", backgroundColor: "#6366f1" },
  catTag: { fontSize: "11px", color: "#64748b", backgroundColor: "#0f172a", padding: "2px 6px", borderRadius: "4px", marginLeft: "6px" },
  metric: { backgroundColor: "#0f172a", borderRadius: "8px", padding: "1rem", textAlign: "center" },
  metricBigVal: { fontSize: "22px", fontWeight: "600", color: "#f8fafc" },
  metricBigLbl: { fontSize: "11px", color: "#64748b", marginTop: "4px" },
  uploadArea: { display: "block", border: "1px dashed #334155", borderRadius: "12px", padding: "2rem", textAlign: "center", cursor: "pointer" },
  modelInfo: { display: "flex", justifyContent: "space-between", fontSize: "12px", color: "#64748b", marginTop: "8px" },
};